// [BUG-FIX 2026-05-16 / Bug 3] Full i18n migration of the landing page.
// Previously the file used `isAr ? 'arabic' : 'french'` ternaries throughout,
// which (a) had no English path at all and (b) didn't react to i18n.changeLanguage
// because the local `lang` state was the source of truth, not i18next.
//
// Rewrite: every visible string now goes through t('lp_*'). The local language
// switcher is kept (visitors land on /landing without auth so the i18next state
// may not have been hydrated by an existing user preference) but it now ALSO
// pushes the next locale through i18n.changeLanguage AND sets <html dir>/lang.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap, School, Users, BarChart3, MessageSquare, Calendar,
  Shield, Zap, CheckCircle, ArrowRight, Star, Globe, Menu, X,
  BookOpen, UserCheck, TrendingUp, Bell, FileText, Smartphone,
  ChevronDown, Award, Clock, HeartHandshake, LayoutDashboard,
  PieChart, DollarSign, ClipboardList
} from 'lucide-react';
import Logo from './Logo';
import SchoolRegister from './SchoolRegister';
import { DarkModeToggle } from './DarkMode';
import usePlatformContact from '../hooks/usePlatformContact';
import { formatNumber } from '../lib/money';
import { setLanguage } from '../lib/preferences';

/* ── Animated counter ──────────────────────────────────────────── */
const Counter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(end / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 25);
    return () => clearInterval(timer);
  }, [end]);
  return <span>{formatNumber(count)}{suffix}</span>;
};

/* ── Role Card ─────────────────────────────────────────────────── */
const RoleCard = ({ icon: Icon, title, color, bg, desc, features }) => (
  <div className={`relative rounded-3xl p-7 border-2 ${bg} hover:shadow-2xl transition-all duration-300 group hover:-translate-y-1`}>
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${color} shadow-lg`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <h3 className="text-xl font-black text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 text-sm leading-relaxed mb-4">{desc}</p>
    <ul className="space-y-2">
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          {f}
        </li>
      ))}
    </ul>
  </div>
);

const LANG_CYCLE = { ar: 'fr', fr: 'en', en: 'ar' };
const LANG_LABEL = { ar: 'FR', fr: 'EN', en: 'عربي' };

const LandingPage = ({ onGoToLogin }) => {
  const { t, i18n } = useTranslation();
  const contact = usePlatformContact();
  const [showRegister, setShowRegister] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const currentLang = (i18n.language || 'ar').slice(0, 2);
  const isRtl = currentLang === 'ar';

  // [Bug 3 fix] The toggle now cycles ar → fr → en → ar through i18next AND
  // updates <html dir>/lang so RTL flips correctly on the landing page itself.
  const handleLangSwitch = () => {
    const next = LANG_CYCLE[currentLang] || 'ar';
    setLanguage(next);  // [UI-PREFS 2026-10-31] stores the choice; i18n sets <html lang/dir>
  };

  if (showRegister) {
    return <SchoolRegister onBack={() => setShowRegister(false)} onSuccess={() => onGoToLogin()} />;
  }

  const roles = [
    {
      icon: LayoutDashboard,
      title:    t('lp_role_super_title'),
      color:    'bg-gradient-to-br from-teal-600 to-teal-800',
      bg:       'border-teal-100 bg-teal-50/50',
      desc:     t('lp_role_super_desc'),
      features: t('lp_role_super_features', { returnObjects: true }),
    },
    {
      icon: School,
      title:    t('lp_role_admin_title'),
      color:    'bg-gradient-to-br from-teal-500 to-teal-700',
      bg:       'border-teal-100 bg-teal-50/50',
      desc:     t('lp_role_admin_desc'),
      features: t('lp_role_admin_features', { returnObjects: true }),
    },
    {
      icon: UserCheck,
      title:    t('lp_role_teacher_title'),
      color:    'bg-gradient-to-br from-blue-500 to-blue-700',
      bg:       'border-blue-100 bg-blue-50/50',
      desc:     t('lp_role_teacher_desc'),
      features: t('lp_role_teacher_features', { returnObjects: true }),
    },
    {
      icon: BookOpen,
      title:    t('lp_role_student_title'),
      color:    'bg-gradient-to-br from-green-500 to-green-700',
      bg:       'border-green-100 bg-green-50/50',
      desc:     t('lp_role_student_desc'),
      features: t('lp_role_student_features', { returnObjects: true }),
    },
    {
      icon: HeartHandshake,
      title:    t('lp_role_parent_title'),
      color:    'bg-gradient-to-br from-orange-500 to-orange-600',
      bg:       'border-orange-100 bg-orange-50/50',
      desc:     t('lp_role_parent_desc'),
      features: t('lp_role_parent_features', { returnObjects: true }),
    },
  ];

  const features = [
    { icon: Users,         color: 'from-teal-600 to-teal-700',   title: t('lp_feat_users_title'),     desc: t('lp_feat_users_desc') },
    { icon: BarChart3,     color: 'from-teal-500 to-teal-600',   title: t('lp_feat_reports_title'),   desc: t('lp_feat_reports_desc') },
    { icon: Calendar,      color: 'from-blue-500 to-blue-600',   title: t('lp_feat_schedule_title'),  desc: t('lp_feat_schedule_desc') },
    { icon: MessageSquare, color: 'from-green-500 to-green-600', title: t('lp_feat_comm_title'),      desc: t('lp_feat_comm_desc') },
    { icon: Shield,        color: 'from-orange-500 to-orange-600', title: t('lp_feat_attend_title'),  desc: t('lp_feat_attend_desc') },
    { icon: DollarSign,    color: 'from-red-500 to-red-600',     title: t('lp_feat_finance_title'),   desc: t('lp_feat_finance_desc') },
    { icon: FileText,      color: 'from-teal-500 to-teal-600',   title: t('lp_feat_export_title'),    desc: t('lp_feat_export_desc') },
    { icon: Smartphone,    color: 'from-pink-500 to-pink-600',   title: t('lp_feat_pwa_title'),       desc: t('lp_feat_pwa_desc') },
    { icon: Bell,          color: 'from-amber-500 to-amber-600', title: t('lp_feat_notif_title'),     desc: t('lp_feat_notif_desc') },
  ];

  const faqs = [
    { q: t('lp_faq_card_q'),    a: t('lp_faq_card_a') },
    { q: t('lp_faq_limits_q'),  a: t('lp_faq_limits_a') },
    { q: t('lp_faq_secure_q'),  a: t('lp_faq_secure_a') },
    { q: t('lp_faq_langs_q'),   a: t('lp_faq_langs_a') },
    { q: t('lp_faq_import_q'),  a: t('lp_faq_import_a') },
  ];

  // Nav links + plan cards extracted so they can be used in both desktop + mobile nav.
  const navLinks = [
    ['#features', t('lp_nav_features')],
    ['#roles',    t('lp_nav_roles')],
    ['#pricing',  t('lp_nav_pricing')],
    ['#faq',      t('lp_nav_faq')],
  ];

  return (
    <div className="min-h-screen bg-white font-sans" dir={isRtl ? 'rtl' : 'ltr'} style={{ fontFamily: "'Cairo', 'Inter', sans-serif" }}>

      {/* ── Navbar ─────────────────────────────────────────────── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-10 h-10 rounded-xl shadow-lg shadow-teal-200" />
            <span className="text-2xl font-black bg-gradient-to-r from-teal-700 to-teal-600 bg-clip-text text-transparent">EduGate</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-gray-600 font-medium text-sm">
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} className="hover:text-teal-600 transition-colors">{label}</a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <DarkModeToggle />
            <button onClick={handleLangSwitch}
                    className="px-4 py-2 text-sm font-bold border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    title={t('lp_lang_switch_title')}>
              {LANG_LABEL[currentLang] || 'FR'}
            </button>
            <button onClick={onGoToLogin} className="px-5 py-2 text-teal-700 font-semibold hover:bg-teal-50 rounded-xl transition-colors text-sm">
              {t('lp_nav_login')}
            </button>
            <button onClick={() => setShowRegister(true)} className="px-5 py-2 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold rounded-xl shadow-lg shadow-teal-200 hover:shadow-xl hover:scale-105 transition-all text-sm">
              {t('lp_nav_start_free')}
            </button>
          </div>

          <button className="md:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden px-6 pb-4 pt-2 flex flex-col gap-2 bg-white border-t border-gray-100">
            {navLinks.slice(0, 3).map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMobileMenu(false)} className="py-2 text-gray-700 font-medium">{label}</a>
            ))}
            <button onClick={handleLangSwitch} className="py-2 text-gray-700 font-medium text-start">
              {LANG_LABEL[currentLang] || 'FR'}
            </button>
            <button onClick={onGoToLogin} className="py-2 text-teal-600 font-semibold text-start">{t('lp_nav_login')}</button>
            <button onClick={() => setShowRegister(true)} className="py-3 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold rounded-xl">{t('lp_mobile_start_free_90')}</button>
          </div>
        )}
      </nav>

      {/* ── Hero ──────────────────────────────────────────────── */}
      {/* [UI-PREFS 2026-10-31] Tailwind stops instead of inline hex, so the hero follows the theme. */}
      <section className="pt-28 pb-20 relative overflow-hidden bg-gradient-to-br from-purple-50 via-indigo-50 to-white">
        <div className="absolute top-10 left-4 w-80 h-80 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDuration:'4s'}} />
        <div className="absolute top-32 right-4 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse" style={{animationDuration:'6s'}} />
        <div className="absolute bottom-0 left-1/2 w-60 h-60 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDuration:'5s'}} />

        <div className="max-w-6xl mx-auto px-6 text-center relative">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 px-5 py-2 rounded-full text-sm font-bold mb-8 border border-teal-200">
            <Zap className="w-4 h-4 text-teal-600" />
            {t('lp_hero_badge')}
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-tight mb-6 tracking-tight">
            {t('lp_hero_title_1')}<br />
            <span className="bg-gradient-to-r from-teal-700 via-teal-600 to-teal-500 bg-clip-text text-transparent">
              {t('lp_hero_title_2')}
            </span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('lp_hero_subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button
              onClick={() => setShowRegister(true)}
              className="group px-9 py-4 bg-gradient-to-r from-teal-800 to-teal-700 text-white text-lg font-black rounded-2xl shadow-2xl shadow-teal-300 hover:shadow-teal-400 hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
              {t('lp_hero_cta_trial')}
              <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            </button>
            <button
              onClick={onGoToLogin}
              className="px-9 py-4 border-2 border-gray-200 text-gray-700 text-lg font-semibold rounded-2xl hover:border-teal-300 hover:bg-teal-50/60 transition-all"
            >
              {t('lp_hero_cta_login')}
            </button>
          </div>

          <p className="text-sm text-muted-foreground flex items-center justify-center gap-4 flex-wrap">
            <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> {t('lp_hero_check_1')}</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> {t('lp_hero_check_2')}</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> {t('lp_hero_check_3')}</span>
          </p>
        </div>
      </section>

      {/* ── Stats Bar ─────────────────────────────────────────── */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { end: 500,   suffix: '+',                          label: t('lp_stat_schools'),  Icon: School },
              { end: 50000, suffix: '+',                          label: t('lp_stat_students'), Icon: Users },
              { end: 99,    suffix: '.9%',                        label: t('lp_stat_uptime'),   Icon: Zap },
              { end: 90,    suffix: ` ${t('lp_stat_days_suffix')}`, label: t('lp_stat_trial'),  Icon: Clock },
            ].map((s, i) => (
              <div key={i} className="group">
                <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl flex items-center justify-center"><s.Icon className="w-5 h-5 text-teal-600" /></div>
                <div className="text-4xl font-black bg-gradient-to-r from-teal-700 to-teal-600 bg-clip-text text-transparent">
                  <Counter end={s.end} suffix={s.suffix} />
                </div>
                <div className="text-gray-500 text-sm mt-1 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ─────────────────────────────────────── */}
      <section id="features" className="py-24 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-4 py-2 rounded-full mb-4 tracking-wide uppercase">{t('lp_section_features_eyebrow')}</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('lp_section_features_title')}</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">{t('lp_section_features_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1 cursor-default">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${f.color} shadow-lg`}>
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roles Section ─────────────────────────────────────── */}
      <section id="roles" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-4 py-2 rounded-full mb-4 tracking-wide uppercase">{t('lp_section_roles_eyebrow')}</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('lp_section_roles_title')}</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">{t('lp_section_roles_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role, i) => (
              <RoleCard key={i} {...role} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Reports Highlight ─────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-4 py-2 rounded-full mb-5 tracking-wide uppercase">{t('lp_reports_eyebrow')}</span>
              <h2 className="text-4xl font-black text-gray-900 mb-5">{t('lp_reports_title')}</h2>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed">{t('lp_reports_subtitle')}</p>
              <div className="space-y-4">
                {[
                  { icon: TrendingUp,    title: t('lp_report_acad_title'),     desc: t('lp_report_acad_desc') },
                  { icon: ClipboardList, title: t('lp_report_attend_title'),   desc: t('lp_report_attend_desc') },
                  { icon: PieChart,      title: t('lp_report_finance_title'),  desc: t('lp_report_finance_desc') },
                  { icon: Award,         title: t('lp_report_top_title'),      desc: t('lp_report_top_desc') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{item.title}</div>
                      <div className="text-sm text-gray-500 mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 rounded-3xl p-8 text-white shadow-2xl shadow-teal-300">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <p className="text-teal-200 text-sm font-medium">{t('lp_reports_card_school')}</p>
                    <h3 className="text-2xl font-black mt-1">{t('lp_reports_card_title')}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                    <BarChart3 className="w-7 h-7" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { label: t('lp_reports_stat_students'), val: '342',            trend: '+12' },
                    { label: t('lp_reports_stat_attend'),   val: '94.2%',          trend: '+2.1%' },
                    { label: t('lp_reports_stat_avg'),      val: '78.5',           trend: '+3.4' },
                    { label: t('lp_reports_stat_fees'),     val: '1,840,000 FCFA', trend: '+5%' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                      <p className="text-teal-200 text-xs mb-2">{stat.label}</p>
                      <p className="text-2xl font-black">{stat.val}</p>
                      <p className="text-green-300 text-xs mt-1 font-medium">↑ {stat.trend} {t('lp_reports_this_month')}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-teal-200 text-xs mb-3 font-medium">{t('lp_reports_class_perf')}</p>
                  <div className="space-y-2">
                    {[['6A', 85, 'bg-green-400'],['5B', 79, 'bg-blue-400'],['4A', 72, 'bg-yellow-400'],['3C', 68, 'bg-orange-400']].map(([cls, pct, color], i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-teal-200 w-6">{cls}</span>
                        <div className="flex-1 bg-white/20 rounded-full h-2">
                          <div className={`h-2 rounded-full ${color}`} style={{width:`${pct}%`, transition:'width 1s'}} />
                        </div>
                        <span className="text-xs text-white font-bold">{pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-gray-100">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('lp_reports_export_label')}</p>
                  <p className="text-sm font-bold text-gray-900">PDF • Excel</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────── */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-bold px-4 py-2 rounded-full mb-4 tracking-wide uppercase">{t('lp_pricing_eyebrow')}</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('lp_pricing_title')}</h2>
            <p className="text-muted-foreground text-lg">{t('lp_pricing_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            {[
              {
                name:     t('lp_plan_trial_name'),
                price:    t('lp_plan_trial_price'),
                period:   t('lp_plan_trial_period'),
                badge:    null,
                border:   'border-gray-200',
                bg:       'bg-white',
                btn:      'border-2 border-gray-200 text-gray-700 hover:bg-gray-50',
                features: t('lp_plan_trial_features', { returnObjects: true }),
              },
              {
                name:     t('lp_plan_basic_name'),
                price:    '50,000 FCFA',
                period:   t('lp_plan_basic_period'),
                badge:    t('lp_plan_popular_badge'),
                border:   'border-teal-500',
                bg:       'bg-white',
                btn:      'bg-gradient-to-r from-teal-800 to-teal-700 text-white shadow-lg shadow-teal-200',
                features: t('lp_plan_basic_features', { returnObjects: true }),
                popular:  true,
              },
              {
                name:     t('lp_plan_pro_name'),
                price:    '100,000 FCFA',
                period:   t('lp_plan_pro_period'),
                badge:    null,
                border:   'border-gray-200',
                bg:       'bg-white',
                btn:      'border-2 border-gray-200 text-gray-700 hover:bg-gray-50',
                features: t('lp_plan_pro_features', { returnObjects: true }),
              },
            ].map((plan, i) => (
              <div key={i} className={`relative rounded-3xl border-2 p-8 ${plan.border} ${plan.bg} ${plan.popular ? 'shadow-2xl shadow-teal-100 scale-105' : 'shadow-sm'} transition-all hover:shadow-xl`}>
                {plan.badge && (
                  <div className="absolute -top-4 right-1/2 translate-x-1/2 bg-gradient-to-r from-teal-800 to-teal-700 text-white text-xs font-black px-5 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                    <Star className="w-3 h-3" /> {plan.badge}
                  </div>
                )}
                <h3 className="text-xl font-black text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-black text-gray-900">{plan.price}</span>
                </div>
                <div className="text-muted-foreground text-sm mb-6">{plan.period}</div>
                <ul className="space-y-3 mb-8">
                  {(Array.isArray(plan.features) ? plan.features : []).map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowRegister(true)}
                  className={`w-full py-3.5 rounded-2xl font-bold transition-all hover:scale-105 ${plan.btn}`}
                >
                  {t('lp_plan_cta')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-4 py-2 rounded-full mb-4 tracking-wide uppercase">{t('lp_faq_eyebrow')}</span>
            <h2 className="text-4xl font-black text-gray-900">{t('lp_faq_title')}</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className={`w-full flex items-center justify-between p-5 ${isRtl ? 'text-right' : 'text-left'}`}
                >
                  <span className="font-bold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-gray-500 text-sm leading-relaxed border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="py-28 relative overflow-hidden" style={{background:'linear-gradient(135deg,#0F766E 0%,#4f46e5 50%,#2563eb 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-8 right-16 w-48 h-48 bg-white rounded-full" />
          <div className="absolute bottom-8 left-8 w-64 h-64 bg-white rounded-full" />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center text-white relative">
          <div className="w-16 h-16 mx-auto mb-6 bg-white/20 rounded-2xl flex items-center justify-center"><GraduationCap className="w-9 h-9 text-white" /></div>
          <h2 className="text-4xl md:text-5xl font-black mb-5">{t('lp_cta_title')}</h2>
          <p className="text-teal-200 text-xl mb-10 leading-relaxed whitespace-pre-line">{t('lp_cta_subtitle')}</p>
          <button
            onClick={() => setShowRegister(true)}
            className="px-12 py-5 bg-white text-teal-700 font-black text-xl rounded-2xl shadow-2xl hover:scale-105 transition-all"
          >
            {t('lp_cta_button')}
          </button>
          <p className="mt-5 text-teal-300 text-sm">{t('lp_cta_hint')}</p>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer id="contact" className="py-14 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-700 to-teal-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-black text-xl">EduGate</span>
            </div>
            <div className="flex flex-wrap gap-6 text-gray-400 text-sm">
              <a href="#features" className="hover:text-white transition-colors">{t('lp_nav_features')}</a>
              <a href="#roles"    className="hover:text-white transition-colors">{t('lp_nav_roles')}</a>
              <a href="#pricing"  className="hover:text-white transition-colors">{t('lp_nav_pricing')}</a>
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="hover:text-white transition-colors">{contact.email}</a>
              )}
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Globe className="w-4 h-4" />
              <span>عربي | Français | English</span>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 text-center text-gray-600 text-sm">
            © 2025 EduGate. {t('lp_footer_rights')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
